import { SocketClient } from "cbcore-ts"
import { NO, UITextView, UIView, UIViewBroadcastEvent, UIViewController, YES } from "uicore-ts"
import { RHCore } from "../../Core/RHCore"
import { CBDialogViewShower } from "../../Custom components/CB/CBDialogViewShower"
import { RHFocusManager } from "../../Custom components/RH/Keyboard navigation/RHFocusManager"
import { RHPageFocusManager } from "../../Custom components/RH/Keyboard navigation/RHPageFocusManager"
import { RHAlertBannerView } from "../../Custom components/RH/RHAlertBannerView"
import { RHButton } from "../../Custom components/RH/RHButton"
import { RHButtons, RHColors, RHFont, RHSpacing } from "../../Custom components/RH/Utils_slop/RHDesignTokens"
import { RHCompanyAdministrationViewTokens } from "../../Custom components/RH/Utils_slop/RHViewTokens"
import { RHCompanyAffiliation } from "../../SocketClientFunctions"
import { CompanyAdministrationInviteMemberView } from "./CompanyAdministrationInviteMemberView"
import { CompanyAdministrationJoinRequestsView } from "./CompanyAdministrationJoinRequestsView"


export class CompanyAdministrationViewController extends UIViewController {

    readonly procurerIneligibleBannerView = new RHAlertBannerView()
        .configuredWithObject({
            alertType: RHAlertBannerView.type.Warning,
            text: RHCompanyAdministrationViewTokens.procurerIneligibleExplanation,
            textAlignment: UITextView.textAlignment.center,
            hidden: YES
        })
        .addedAsSubviewToView(this.view)

    readonly inviteMemberButton = new RHButton()
        .configuredWithObject({
            titleLabel: { text: "Kutsu liige" },
            variant: "success"
        })
        .addedAsSubviewToView(this.view)

    readonly pendingTitleLabel = new UITextView()
        .configuredWithObject({
            text: "Taotlused ja kutsed",
            textColor: RHColors.textHeading,
            style: {
                fontSize: RHFont.sizeLarge,
                fontWeight: RHFont.weightSemibold
            }
        })
        .addedAsSubviewToView(this.view)

    readonly joinRequestsView = new CompanyAdministrationJoinRequestsView()
        .addedAsSubviewToView(this.view)

    override _focusManager = new RHFocusManager(() => {
        const items: (UIView | RHFocusManager)[] = []
        if (!this.inviteMemberButton.hidden) {
            items.push(this.inviteMemberButton)
        }
        if (!this.joinRequestsView.hidden) {
            items.push(this.joinRequestsView._focusManager!)
        }
        return items
    })

    _activeCompanyAffiliation?: RHCompanyAffiliation
    _requestedCompanyId?: string
    _requestedWorkflowId?: string
    _handledWorkflowId?: string
    _requestsMemberInvitation = NO
    _handledMemberInvitationRequest = NO
    _loadGeneration = 0
    workflowLinkWasConsumed?: () => void
    invitationLinkWasConsumed?: () => void


    constructor(view: UIView) {
        super(view)
        this.inviteMemberButton.controlEventTargetAccumulator.PrimaryActionTriggered = () => {
            this._showInviteMemberDialog()
        }
        this.joinRequestsView.workflowsDidChange = () => {
            this._refreshAfterMutation().then(undefined)
        }
        this._applyProcurerIneligibleBannerVisibility(NO)
        this._applyPendingWorkflowsVisibility(NO)
    }


    set requestedWorkflowId(workflowId: string | undefined) {
        this._requestedWorkflowId = workflowId
        if (!workflowId) {
            this._handledWorkflowId = undefined
        }
        if (
            workflowId &&
            workflowId !== this._handledWorkflowId &&
            this.view.superview
        ) {
            this._load().then(undefined)
        }
    }


    set requestedCompanyId(companyId: string | undefined) {
        this._requestedCompanyId = companyId
    }


    set requestsMemberInvitation(requestsInvitation: boolean) {
        this._requestsMemberInvitation = requestsInvitation
        if (!requestsInvitation) {
            this._handledMemberInvitationRequest = NO
        }
        if (
            requestsInvitation &&
            !this._handledMemberInvitationRequest &&
            this.view.superview
        ) {
            this._load().then(undefined)
        }
    }


    override async viewWillAppear() {
        await super.viewWillAppear()
        RHPageFocusManager.shared.registerContentControllerManager(
            this._focusManager!,
            { focusesFirstItem: NO }
        )
    }


    override async viewWillDisappear() {
        await super.viewWillDisappear()
        RHPageFocusManager.shared.deregisterContentControllerManager(this._focusManager!)
    }


    override async viewDidAppear() {
        await super.viewDidAppear()
        await this._load()
    }


    override viewDidReceiveBroadcastEvent(event: UIViewBroadcastEvent) {
        super.viewDidReceiveBroadcastEvent(event)
        if (
            event.name === RHCore.broadcastEventName.activeCompanyDidChange ||
            event.name === RHCore.broadcastEventName.currentCompanyAffiliationDidChange
        ) {
            this._load().then(undefined)
        }
    }


    async _load() {
        const loadGeneration = ++this._loadGeneration
        this.view.loading = YES
        try {
            await RHCore.sharedInstance.companyAffiliationStore.retrieveMyCompanyAffiliations()
            if (loadGeneration !== this._loadGeneration) {
                return
            }
            const affiliation = RHCore.sharedInstance.companyAffiliationStore.activeCompanyAffiliation
            this._activeCompanyAffiliation = affiliation
            const activeCompanyId = CompanyAdministrationViewController._companyIdString(
                (affiliation?.company)?._id
            )
            if (this._requestedCompanyId && activeCompanyId !== this._requestedCompanyId) {
                this._applyAdministrationVisibility(NO)
                this._rejectRequestedWorkflowLink(
                    "Sul puudub õigus lingis määratud ettevõtte liikmeid hallata."
                )
                this._rejectRequestedMemberInvitation(
                    "Sul puudub õigus lingis määratud ettevõtte liikmeid hallata."
                )
                return
            }

            const canAdminister =
                CompanyAdministrationViewController._canAdministerCompany(affiliation)
            this._applyAdministrationVisibility(canAdminister)
            if (!canAdminister || !affiliation) {
                this._rejectRequestedWorkflowLink(
                    "Liitumistaotlust saab avada ainult ettevõtte otsene peakasutaja."
                )
                this._rejectRequestedMemberInvitation(
                    "Liikmeid saab kutsuda ainult ettevõtte otsene peakasutaja."
                )
                return
            }

            const companyId = CompanyAdministrationViewController._companyIdString(
                affiliation.company?._id
            )
            if (!companyId) {
                return
            }
            this.joinRequestsView.companyId = companyId
            const response = await SocketClient.ListCompanyMembersAndPending({ companyId })
            if (
                loadGeneration !== this._loadGeneration ||
                RHCore.sharedInstance.companyAffiliationStore.activeCompanyId !== companyId
            ) {
                return
            }
            if (!response.result?.success) {
                CBDialogViewShower.alert(
                    RHCore.sharedInstance.errorMessageFromResponse(
                        response,
                        "Liikmete laadimine ebaõnnestus."
                    ) ?? "Liikmete laadimine ebaõnnestus."
                )
                return
            }

            this.joinRequestsView.updateWithWorkflowSources(
                response.result.workflows,
                // Company registration requests have no place in a per-company join/invite
                // table - they concern a company that doesn't exist yet.
                RHCore.sharedInstance.companyAffiliationStore.membershipWorkflows.filter(
                    workflow => workflow.kind === "membership"
                )
            )
            this._applyAdministrationVisibility(canAdminister)
            this._openRequestedWorkflowIfAvailable()
            this._showRequestedMemberInvitationIfAvailable()
        }
        catch (error) {
            console.error("Company members failed to load:", error)
            CBDialogViewShower.alert("Liikmete laadimine ebaõnnestus.")
        }
        finally {
            if (loadGeneration === this._loadGeneration) {
                this.view.loading = NO
                this.view.setNeedsLayoutUpToRootView()
            }
        }
    }


    _applyAdministrationVisibility(canAdminister: boolean) {
        this.inviteMemberButton.hidden = !canAdminister
        this._applyProcurerIneligibleBannerVisibility(
            canAdminister && this._activeCompanyAffiliation?.isEligibleToActAsProcurer === NO
        )
        this._applyPendingWorkflowsVisibility(canAdminister)
        this.view.setNeedsLayout()
    }


    // Detaching (not just .hidden) skips its unassigned layoutViewSubviews frame - left attached it stretched to fill the page.
    _applyProcurerIneligibleBannerVisibility(isVisible: boolean) {
        this.procurerIneligibleBannerView.hidden = !isVisible
        if (isVisible) {
            if (this.procurerIneligibleBannerView.superview !== this.view) {
                this.procurerIneligibleBannerView.addedAsSubviewToView(this.view)
            }
            return
        }
        this.procurerIneligibleBannerView.removeFromSuperview()
    }


    _applyPendingWorkflowsVisibility(isVisible: boolean) {
        this.pendingTitleLabel.hidden = !isVisible
        this.joinRequestsView.hidden = !isVisible
        if (isVisible) {
            if (this.pendingTitleLabel.superview !== this.view) {
                this.pendingTitleLabel.addedAsSubviewToView(this.view)
            }
            if (this.joinRequestsView.superview !== this.view) {
                this.joinRequestsView.addedAsSubviewToView(this.view)
            }
            return
        }
        this.pendingTitleLabel.removeFromSuperview()
        this.joinRequestsView.removeFromSuperview()
    }


    _showInviteMemberDialog() {
        const affiliation = this._activeCompanyAffiliation
        if (!affiliation || affiliation.source === "relationship" || affiliation.isAdministrator !== YES) {
            CBDialogViewShower.alert("Liikmeid saab kutsuda ainult ettevõtte otsene peakasutaja.")
            return
        }
        const companyId = CompanyAdministrationViewController._companyIdString(
            affiliation.company?._id
        )
        if (!companyId) {
            return
        }
        const dialogView = new CompanyAdministrationInviteMemberView()
        dialogView.companyId = companyId
        dialogView.isEligibleToActAsProcurer = affiliation.isEligibleToActAsProcurer
        dialogView.selectDefaultRoleForApplicationMode(RHCore.sharedInstance.applicationMode)
        dialogView.invitationDidComplete = () => this._refreshAfterMutation()
        CBDialogViewShower.showDialog(dialogView, undefined, NO)
    }


    _openRequestedWorkflowIfAvailable() {
        const workflowId = this._requestedWorkflowId
        if (!workflowId || workflowId === this._handledWorkflowId) {
            return
        }
        this._handledWorkflowId = workflowId
        this.workflowLinkWasConsumed?.()
        if (!this.joinRequestsView.openWorkflowWithId(workflowId)) {
            CBDialogViewShower.alert(
                "Taotlus või kutse on juba töödeldud või ei kuulu aktiivsele ettevõttele."
            )
        }
    }


    _rejectRequestedWorkflowLink(message: string) {
        const workflowId = this._requestedWorkflowId
        if (!workflowId || workflowId === this._handledWorkflowId) {
            return
        }
        this._handledWorkflowId = workflowId
        this.workflowLinkWasConsumed?.()
        CBDialogViewShower.alert(message)
    }


    _showRequestedMemberInvitationIfAvailable() {
        if (!this._requestsMemberInvitation || this._handledMemberInvitationRequest) {
            return
        }
        this._handledMemberInvitationRequest = YES
        this.invitationLinkWasConsumed?.()
        this._showInviteMemberDialog()
    }


    _rejectRequestedMemberInvitation(message: string) {
        if (!this._requestsMemberInvitation || this._handledMemberInvitationRequest) {
            return
        }
        this._handledMemberInvitationRequest = YES
        this.invitationLinkWasConsumed?.()
        CBDialogViewShower.alert(message)
    }


    async _refreshAfterMutation() {
        try {
            await RHCore.sharedInstance.refreshCurrentCompanyAffiliation()
            await this._load()
        }
        catch (error) {
            console.error("Company administration refresh after successful mutation failed:", error)
            CBDialogViewShower.alert(
                "Toiming õnnestus, kuid vaate värskendamine ebaõnnestus. " +
                "Palun värskenda lehte."
            )
        }
    }


    override layoutViewSubviews() {
        super.layoutViewSubviews()
        const padding = this.core.paddingLength
        const bounds = this.view.contentBoundsWithInsets(padding, padding, padding, 0)

        const inviteMemberButtonRow = bounds
            .IF(!this.procurerIneligibleBannerView.hidden)
            .rectangleWithHeight(this.procurerIneligibleBannerView)
            .assignedAsFrameOfView(this.procurerIneligibleBannerView)
            .rectangleForNextRow(RHSpacing.medium, 0)
            .ELSE()
            .rectangleWithHeight(0)
            .ENDIF()
            .rectangleForNextRow(0, RHButtons.compactPrimaryHeight)
        const inviteMemberButtonWidth = RHSpacing.xxLarge * 3
        this.inviteMemberButton.frame = inviteMemberButtonRow
            .rectangleWithWidth(inviteMemberButtonWidth, 1)
            .rectangleWithHeight(RHButtons.compactPrimaryHeight, 0.5)

        if (
            this.pendingTitleLabel.superview !== this.view ||
            this.joinRequestsView.superview !== this.view
        ) {
            return
        }

        inviteMemberButtonRow
            .rectangleForNextRow(RHSpacing.large, this.pendingTitleLabel)
            .assignedAsFrameOfView(this.pendingTitleLabel)
            .rectangleForNextRow(
                RHSpacing.small,
                RHCompanyAdministrationViewTokens.pendingWorkflowsTableHeight
            )
            .assignedAsFrameOfView(this.joinRequestsView)
    }


    // Company "_id" fields are typed as Mongoose's Types.ObjectId, but arrive over the
    // socket as a plain string once JSON-serialized - this normalizes either shape.
    static _companyIdString(
        rawCompanyId: string | { toString(): string } | undefined
    ): string | undefined {
        if (!rawCompanyId) {
            return undefined
        }
        if (typeof rawCompanyId === "string") {
            return rawCompanyId
        }
        const stringValue = rawCompanyId.toString()
        if (stringValue !== "[object Object]") {
            return stringValue
        }
        return undefined
    }


    static _canAdministerCompany(
        affiliation: RHCompanyAffiliation | undefined
    ): boolean {
        return !!(
            affiliation &&
            affiliation.source !== "relationship" &&
            affiliation.membershipStatus === "active" &&
            affiliation.isAdministrator === YES
        )
    }

}
